'use client'

import React from 'react'
import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form'

import { phoneRules, phoneRulesOptional } from '@/shared/lib/validation/phone'
import type { InputProps } from '@/shared/ui/Input'
import { MaskedPhoneInput } from '@/shared/ui/MaskedInput/MaskedPhoneInput'

/**
 * React Hook Form Phone Input component
 * Включает маску ввода, автоматическую валидацию и нормализацию телефона
 *
 * @example
 * ```tsx
 * // Обязательный телефон
 * <RHFPhoneInput
 *   name="phone"
 *   control={control}
 *   error={errors.phone}
 * />
 *
 * // Необязательный телефон
 * <RHFPhoneInput
 *   name="phone"
 *   control={control}
 *   error={errors.phone}
 *   required={false}
 * />
 * ```
 */

export interface RHFPhoneInputProps<T extends FieldValues> extends Omit<InputProps, 'name' | 'type'> {
	name: Path<T>
	control: Control<T>
	error?: FieldError
	label?: string
	required?: boolean
}

export const RHFPhoneInput = <T extends FieldValues>({
	name,
	control,
	error,
	label = 'Номер телефона',
	required = true,
	...inputProps
}: RHFPhoneInputProps<T>) => {
	const validationRules = required ? phoneRules : phoneRulesOptional

	return (
		<Controller
			name={name}
			control={control}
			rules={validationRules}
			render={({ field, fieldState }) => {
				const currentError = fieldState.error || error

				return (
					<MaskedPhoneInput
						{...field}
						{...inputProps}
						name={field.name}
						label={label}
						type="tel"
						inputMode="tel"
						autoComplete="tel"
						value={field.value}
						onChange={field.onChange}
						onClear={() => field.onChange('')}
						onBlur={field.onBlur}
						isError={!!currentError}
						helperText={currentError?.message}
					/>
				)
			}}
		/>
	)
}
